import { walletConnectProvider } from "@web3modal/wagmi";
import { createWeb3Modal, defaultWagmiConfig } from "@web3modal/wagmi/react";
import { BigNumber, Contract, ethers } from "ethers";
import React, { createContext, useContext, useEffect, useState } from "react";
import { Config, WagmiConfig, configureChains } from "wagmi";
import { WalletConnectConnector } from "wagmi/connectors/walletConnect";
import { publicProvider } from "wagmi/providers/public";
import { Web3TsConfig } from "../IWeb3Ts";
import { useContract } from "../hooks/useContract";
type BuyMetadata = {
 minBuy?: number;
 maxBuy?: number;
 minBuyUSDT?: number;
 maxBuyUSDT?: number;
 salePrice?: number;
 salePriceUSDT?: number;
};
type IWeb3TsContext = {
 gasFee: number;
 contract?: Contract;
 contractBuy?: Contract;
 contractUsdt?: Contract;
 setContract: (value?: Contract) => void;
 setContractBuy: (value?: Contract) => void;
 setContractUsdt: (value?: Contract) => void;
 config: Web3TsConfig;
 setConfig: (config: Web3TsConfig) => void;
 walletConnectConnector: WalletConnectConnector;
 buyMetadata: BuyMetadata;
 setBuyMetadata: (val: BuyMetadata) => void;
};

const context = createContext<IWeb3TsContext>({} as IWeb3TsContext);

export const Web3TsProvider = (
 p: React.PropsWithChildren & { config: Web3TsConfig }
) => {
 const [contract, setContract] = useState<Contract>();
 const [buyMetadata, setBuyMetadata] = useState<BuyMetadata>({});
 const [contractBuy, setContractBuy] = useState<Contract>();
 const [contractUsdt, setContractUsdt] = useState<Contract>();
 const [config, setConfig] = useState<Web3TsConfig>(p.config);
 const [wagmiConfig, setWagmiConfig] = useState<Config<never, never>>();
 const [walletConnectConnector, setWalletConnectConnector] =
  useState<WalletConnectConnector>(undefined!);
 useEffect(() => {
  if (!p.config) return;
  if (!p.config.buyContract.buyUSDTMethod) {
   p.config.buyContract.buyUSDTMethod =
    p.config.buyContract.buyUSDTMethod ??
    p.config.buyContract.buyMethod + "USDT";
  }
  setConfig({ ...p.config });
 }, [p.config]);
 useEffect(() => {
  const { projectId, metadata, walletIds, chains: cChains } = config;
  if (!(projectId && metadata && walletIds && cChains)) return;
  const { chains, webSocketPublicClient } = configureChains(cChains, [
   walletConnectProvider({ projectId: projectId }),
   publicProvider(),
  ]);
  const walletConnectConnector = new WalletConnectConnector({
   chains,
   options: {
    projectId: projectId,
    metadata: metadata,
   },
  });
  const wagmiConfig = defaultWagmiConfig({
   chains,
   projectId: projectId,
   metadata: metadata,
  });
  wagmiConfig.args.autoConnect = false;
  wagmiConfig.args.webSocketPublicClient = webSocketPublicClient;
  wagmiConfig.connectors.push(walletConnectConnector);
  createWeb3Modal({
   wagmiConfig,
   projectId: projectId,
   chains,
   featuredWalletIds: walletIds,
  });
  setWalletConnectConnector(walletConnectConnector);
  setWagmiConfig(wagmiConfig as never);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [config.projectId, config.walletIds, config.metadata, config.chains]);
 return (
  <context.Provider
   value={{
    buyMetadata,
    setBuyMetadata,
    config,
    setConfig,
    gasFee: 0.0004,
    walletConnectConnector,
    contract,
    contractBuy,
    contractUsdt,
    setContract,
    setContractBuy,
    setContractUsdt,
   }}
  >
   {wagmiConfig && (
    <WagmiConfig config={wagmiConfig}>
     <InnerContext />
     {p.children}
    </WagmiConfig>
   )}
  </context.Provider>
 );
};

const InnerContext = () => {
 const { contract, contractBuy, contractUsdt } = useContract();
 const {
  setContract,
  setContractBuy,
  setContractUsdt,
  buyMetadata,
  setBuyMetadata,
 } = useWeb3Ts();
 useEffect(() => {
  setContract(contract);
 }, [contract]);
 useEffect(() => {
  setContractBuy(contractBuy);
  if (contractBuy) {
   const _buyMetadata = { ...buyMetadata };
   const promises: Promise<never>[] = [];
   if (contractBuy.minBuy) {
    promises.push(
     contractBuy
      .minBuy()
      .then((resp: BigNumber) => {
       const value = ethers.utils.formatUnits(resp);
       _buyMetadata.minBuy = +value;
      })
      .catch(() => 0)
    );
   }
   if (contractBuy.maxBuy) {
    promises.push(
     contractBuy
      .maxBuy()
      .then((resp: BigNumber) => {
       const value = ethers.utils.formatUnits(resp);
       _buyMetadata.maxBuy = +value;
      })
      .catch(() => 0)
    );
   }
   if (contractBuy.minBuyUSDT) {
    promises.push(
     contractBuy
      .minBuyUSDT()
      .then((resp: BigNumber) => {
       const value = ethers.utils.formatUnits(resp);
       _buyMetadata.minBuyUSDT = +value;
      })
      .catch(() => 0)
    );
   }
   if (contractBuy.maxBuyUSDT) {
    promises.push(
     contractBuy
      .maxBuyUSDT()
      .then((resp: BigNumber) => {
       const value = ethers.utils.formatUnits(resp);

       _buyMetadata.maxBuyUSDT = +value;
      })
      .catch(() => 0)
    );
   }
   if (contractBuy.salePrice) {
    promises.push(
     contractBuy
      .salePrice()
      .then((resp: BigNumber) => {
       const value = ethers.utils.formatUnits(resp,0);
       _buyMetadata.salePrice = +value;
      })
      .catch(() => 0)
    );
   }
   if (contractBuy.salePriceUSDT) {
    promises.push(
     contractBuy
      .salePriceUSDT()
      .then((resp: BigNumber) => {
       const value = ethers.utils.formatUnits(resp,0);
       _buyMetadata.salePriceUSDT = +value;
      })
      .catch(() => 0)
    );
   }
   if (promises.length) {
    Promise.all(promises).then(() => setBuyMetadata(_buyMetadata));
   }
  }
 }, [contractBuy]);
 useEffect(() => {
  setContractUsdt(contractUsdt);
 }, [contractUsdt]);
 return <></>;
};

export const useWeb3Ts = () => useContext(context);
